import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CardActions, Button } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import {
  BusinessOutlined,
  LocationOnOutlined,
  GroupsOutlined,
  SettingsOutlined,
  ArrowForwardRounded,
  DomainOutlined,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import OrganizationHeader from "./components/OrganizationHeader";

export default function OrgSettings() {
  const { t } = useTranslation();
  const theme = useTheme();

  const orgSettingsOptions = [
    {
      title: t("orgSettings.organization"),
      description: t("orgSettings.organizationDesc", {
        defaultValue:
          "Configure general organization settings, company information, and global organizational preferences.",
      }),
      icon: <DomainOutlined sx={{ fontSize: 40 }} />,
      path: "/orgsettings/organization",
      color: theme.palette.warning.main,
      aria: t("orgSettings.organizationAria"),
    },
    {
      title: t("orgSettings.departments"),
      description: t("orgSettings.departmentsDesc", {
        defaultValue:
          "Manage organizational departments, their hierarchies, and departmental structures within your company.",
      }),
      icon: <BusinessOutlined sx={{ fontSize: 40 }} />,
      path: "/orgsettings/departments",
      color: theme.palette.primary.main,
      aria: t("orgSettings.departmentsAria"),
    },
    {
      title: t("orgSettings.sites"),
      description: t("orgSettings.sitesDesc", {
        defaultValue:
          "Configure and manage physical locations, sites, and facilities where your organization operates.",
      }),
      icon: <LocationOnOutlined sx={{ fontSize: 40 }} />,
      path: "/orgsettings/sites",
      color: theme.palette.secondary.main,
      aria: t("orgSettings.sitesAria"),
    },
    {
      title: t("orgSettings.userGroups"),
      description: t("orgSettings.userGroupsDesc", {
        defaultValue:
          "Define user groups, roles, and permission sets to control access levels and organizational responsibilities.",
      }),
      icon: <GroupsOutlined sx={{ fontSize: 40 }} />,
      path: "/orgsettings/usergroups",
      color: theme.palette.info.main,
      aria: t("orgSettings.userGroupsAria"),
    },
  ];

  return (
    <>
      <OrganizationHeader
        icon={SettingsOutlined}
        title={t("orgSettings.headerTitle", {
          defaultValue: "Organization Settings",
        })}
        subtitle={t("orgSettings.headerSubtitle", {
          defaultValue:
            "Configure departments, sites, user groups, and organizational structure",
        })}
      />

      {/* Organization Settings Options Cards */}
      <Box sx={{ px: 3 }}>
        <Grid container spacing={3}>
          {orgSettingsOptions.map((option, index) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s ease",
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: theme.shadows[8],
                    borderColor: option.color,
                    "& .option-icon": {
                      transform: "scale(1.1)",
                      color: option.color,
                    },
                    "& .option-arrow": {
                      transform: "translateX(8px)",
                    },
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      className="option-icon"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        bgcolor: alpha(option.color, 0.1),
                        border: "2px solid",
                        borderColor: alpha(option.color, 0.2),
                        color: alpha(option.color, 0.7),
                        transition: "all 0.3s ease",
                        mb: 2,
                      }}
                    >
                      {option.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mb: 1, color: "text.primary" }}
                    >
                      {option.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.5,
                        fontSize: "0.875rem",
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    component={Link}
                    to={option.path}
                    variant="contained"
                    fullWidth
                    endIcon={
                      <ArrowForwardRounded
                        className="option-arrow"
                        sx={{
                          fontSize: 18,
                          transition: "transform 0.3s ease",
                        }}
                      />
                    }
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      background: `linear-gradient(135deg, ${option.color} 0%, ${alpha(option.color, 0.8)} 100%)`,
                      "&:hover": {
                        boxShadow: theme.shadows[6],
                        background: `linear-gradient(135deg, ${alpha(option.color, 0.9)} 0%, ${alpha(option.color, 0.7)} 100%)`,
                      },
                      transition: "all 0.3s ease",
                    }}
                    aria-label={option.aria}
                  >
                    {t("orgSettings.configure", { defaultValue: "Configure" })}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
